import React, { Component } from 'react';
import Modal from '../../../components/Modal';
import { urlMap, getAuthHeaders } from '../../../../utils';
import { tokenizeRuns, diffWordTokens, buildDiffTokens } from './diffUtils';

const AUTOSAVE_DEBOUNCE_MS = 800;

/**
 * Owner-side review of writer submissions for a single chapter.
 *
 * The owner sees a read-only word-level diff between their *current* chapter
 * content and the writer's submitted content. The original side is editable
 * with debounced autosave so the owner can manually pull in any wording they
 * like; the diff highlights update live as they type. There is no
 * accept/apply flow — the writer's submission is read-only inspiration.
 *
 * Props:
 * - isOpen: boolean
 * - onClose: () => void
 * - chapter: { id, title }
 * - onReviewed?: () => void  // called after dismissing a submission so parent can refresh counts
 */
export default class ChapterSubmissionsModal extends Component {
  state = {
    loading: false,
    error: null,
    submissions: [],
    selectedDraftId: null,
    review: null, // { draft, originalTitle, originalContent }
    reviewLoading: false,
    reviewError: null,
    decisionPending: false,
    // Live-edited original chapter content (autosaved to drafts.chapter -> chapters.content).
    editedOriginal: '',
    originalDirty: false,
    savingOriginal: false,
    saveError: null,
    lastSavedAt: null,
  };

  autosaveTimer = null;
  // Tracks the latest in-flight save so a stale response can't clobber a newer edit.
  saveSeq = 0;

  componentDidMount() {
    if (this.props.isOpen) this.loadList();
  }

  componentWillUnmount() {
    this.flushAutosave();
  }

  componentDidUpdate(prevProps) {
    if (this.props.isOpen && (!prevProps.isOpen || prevProps.chapter?.id !== this.props.chapter?.id)) {
      this.flushAutosave();
      this.setState({
        selectedDraftId: null,
        review: null,
        reviewError: null,
        editedOriginal: '',
        originalDirty: false,
        savingOriginal: false,
        saveError: null,
        lastSavedAt: null,
      }, () => this.loadList());
    } else if (prevProps.isOpen && !this.props.isOpen) {
      // Modal just closed — make sure pending edits are saved.
      this.flushAutosave();
    }
  }

  loadList = async () => {
    const chapterId = this.props.chapter?.id;
    if (!chapterId) return;
    this.setState({ loading: true, error: null });
    try {
      const res = await fetch(urlMap.draftInboxForChapter(chapterId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const submissions = await res.json();
      this.setState({ submissions: Array.isArray(submissions) ? submissions : [], loading: false });
    } catch (err) {
      this.setState({ error: err.message || 'Failed to load submissions', loading: false });
    }
  };

  selectDraft = async (draftId) => {
    this.flushAutosave();
    this.setState({
      selectedDraftId: draftId,
      reviewLoading: true,
      reviewError: null,
      review: null,
      editedOriginal: '',
      originalDirty: false,
      savingOriginal: false,
      saveError: null,
      lastSavedAt: null,
    });
    try {
      const res = await fetch(urlMap.draftReview(draftId), {
        method: 'GET',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
      });
      if (!res.ok) throw new Error(`HTTP ${res.status}`);
      const review = await res.json();
      this.setState({
        review,
        reviewLoading: false,
        editedOriginal: review?.originalContent || '',
      });
    } catch (err) {
      this.setState({ reviewError: err.message || 'Failed to load submission', reviewLoading: false });
    }
  };

  // ---------- editable original + autosave ----------
  onOriginalChange = (e) => {
    const value = e.target.value;
    this.setState({ editedOriginal: value, originalDirty: true, saveError: null });
    this.scheduleAutosave();
  };

  scheduleAutosave = () => {
    if (this.autosaveTimer) clearTimeout(this.autosaveTimer);
    this.autosaveTimer = setTimeout(this.saveOriginal, AUTOSAVE_DEBOUNCE_MS);
  };

  flushAutosave = () => {
    if (this.autosaveTimer) {
      clearTimeout(this.autosaveTimer);
      this.autosaveTimer = null;
    }
    if (this.state.originalDirty) {
      // Fire-and-forget; component may unmount before this resolves.
      this.saveOriginal();
    }
  };

  saveOriginal = async () => {
    this.autosaveTimer = null;
    const chapterId = this.props.chapter?.id;
    if (!chapterId) return;
    if (!this.state.originalDirty || this.state.savingOriginal) {
      // If a save is already in-flight, leave dirty=true; the in-flight save's
      // completion will not schedule another, but the next keystroke will.
      return;
    }
    const seq = ++this.saveSeq;
    const content = this.state.editedOriginal;
    const title = this.state.review?.originalTitle
      || this.state.review?.draft?.source_chapter_title
      || this.props.chapter?.title;
    this.setState({ savingOriginal: true, saveError: null });
    try {
      const res = await fetch(urlMap.updateChapter(chapterId), {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ title, content }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }
      // If a newer save started after us, don't flip dirty/saved state.
      if (seq !== this.saveSeq) return;
      this.setState((prev) => ({
        savingOriginal: false,
        // Only clear dirty if no further keystrokes happened since save started.
        originalDirty: prev.editedOriginal !== content ? true : false,
        lastSavedAt: Date.now(),
      }), () => {
        // If user kept typing during the save, schedule the next one.
        if (this.state.originalDirty) this.scheduleAutosave();
      });
    } catch (err) {
      if (seq !== this.saveSeq) return;
      this.setState({ savingOriginal: false, saveError: err.message || 'Autosave failed' });
    }
  };

  // ---------- dismiss submission (no approve flow) ----------
  dismissSubmission = async () => {
    const draftId = this.state.selectedDraftId;
    if (!draftId || this.state.decisionPending) return;
    this.flushAutosave();
    this.setState({ decisionPending: true, reviewError: null });
    try {
      const res = await fetch(urlMap.draftReview(draftId), {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        credentials: 'include',
        body: JSON.stringify({ decision: 'rejected' }),
      });
      if (!res.ok) {
        const payload = await res.json().catch(() => ({}));
        throw new Error(payload?.error || `HTTP_${res.status}`);
      }
      this.setState({
        decisionPending: false,
        selectedDraftId: null,
        review: null,
        editedOriginal: '',
        originalDirty: false,
        lastSavedAt: null,
      }, () => {
        this.loadList();
        this.props.onReviewed?.();
      });
    } catch (err) {
      this.setState({
        decisionPending: false,
        reviewError: err.message || 'Failed to dismiss submission',
      });
    }
  };

  renderList() {
    const { loading, error, submissions } = this.state;
    if (loading) return <div className="submissions-modal__empty">Loading submissions…</div>;
    if (error) return <div className="submissions-modal__error">Could not load submissions: {error}</div>;
    if (!submissions.length) {
      return <div className="submissions-modal__empty">No pending submissions for this chapter.</div>;
    }

    return (
      <ul className="submissions-modal__list">
        {submissions.map((s) => {
          const isActive = this.state.selectedDraftId === s.id;
          const submittedAt = s.submitted_at ? new Date(s.submitted_at).toLocaleString() : '—';
          return (
            <li key={s.id} className={`submissions-modal__item${isActive ? ' is-active' : ''}`}>
              <button
                type="button"
                className="submissions-modal__item-btn"
                onClick={() => this.selectDraft(s.id)}
              >
                <div className="submissions-modal__item-title">{s.chapter_title || 'Untitled draft'}</div>
                <div className="submissions-modal__item-meta">
                  Submitted {submittedAt}
                </div>
                {s.submission_message && (
                  <div className="submissions-modal__item-message">“{s.submission_message}”</div>
                )}
              </button>
            </li>
          );
        })}
      </ul>
    );
  }

  renderSaveStatus() {
    const { savingOriginal, originalDirty, saveError, lastSavedAt } = this.state;
    if (saveError) {
      return <span className="submissions-review__save-status submissions-review__save-status--error">Autosave failed: {saveError}</span>;
    }
    if (savingOriginal) {
      return <span className="submissions-review__save-status">Saving…</span>;
    }
    if (originalDirty) {
      return <span className="submissions-review__save-status">Unsaved changes</span>;
    }
    if (lastSavedAt) {
      return <span className="submissions-review__save-status submissions-review__save-status--saved">Saved</span>;
    }
    return null;
  }

  renderReview() {
    const {
      review, reviewLoading, reviewError, decisionPending, selectedDraftId, editedOriginal,
    } = this.state;
    if (!selectedDraftId) {
      return (
        <div className="submissions-modal__placeholder">
          Select a submission on the left to review.
        </div>
      );
    }
    if (reviewLoading) return <div className="submissions-modal__empty">Loading…</div>;
    if (reviewError) return <div className="submissions-modal__error">Could not load: {reviewError}</div>;
    if (!review) return null;

    const { draft, originalTitle } = review;

    // Recompute diff between LIVE edited original and submitted content.
    const submittedContent = draft?.chapter_content || '';
    const origRuns = tokenizeRuns(editedOriginal);
    const subRuns = tokenizeRuns(submittedContent);
    const origWords = origRuns.filter((r) => r.type === 'word').map((r) => r.text);
    const subWords = subRuns.filter((r) => r.type === 'word').map((r) => r.text);
    const ops = diffWordTokens(origWords, subWords);
    const diffCount = ops.filter((o) => o.type !== 'eq').length;

    return (
      <div className="submissions-review">
        <div className="submissions-review__header">
          <h3 className="submissions-review__title">{draft.chapter_title || 'Untitled'}</h3>
          {draft.submission_message && (
            <blockquote className="submissions-review__message">
              {draft.submission_message}
            </blockquote>
          )}
          <div className="submissions-review__hint">
            {diffCount === 0
              ? 'No textual differences — your chapter matches this submission.'
              : `${diffCount} word${diffCount === 1 ? '' : 's'} differ. Edit your chapter on the left; the diff updates as you type.`}
          </div>
        </div>

        <div className="submissions-review__pane">
          <div className="submissions-review__columns">
            <section className="submissions-review__col">
              <header className="submissions-review__col-head">
                Your chapter (editable)
                {this.renderSaveStatus()}
              </header>
              <div className="submissions-review__title-row">
                <strong>{originalTitle || draft.source_chapter_title || 'Untitled'}</strong>
              </div>
              <textarea
                className="submissions-review__editor"
                value={editedOriginal}
                onChange={this.onOriginalChange}
                onBlur={this.flushAutosave}
                spellCheck
              />
            </section>
            <section className="submissions-review__col">
              <header className="submissions-review__col-head">Submitted</header>
              <div className="submissions-review__title-row">
                <strong>{draft.chapter_title || 'Untitled'}</strong>
              </div>
              <div className="submissions-review__body submissions-review__body--diff">
                {(() => {
                  const { tokens, trailingWs } = buildDiffTokens({ runs: subRuns, ops, side: 'submitted' });
                  return (
                    <>
                      {tokens.map((t, i) => (
                        <React.Fragment key={i}>
                          {t.ws}
                          {t.highlight ? <span className="diff-added">{t.word}</span> : <span>{t.word}</span>}
                        </React.Fragment>
                      ))}
                      <span>{trailingWs}</span>
                    </>
                  );
                })()}
              </div>
            </section>
          </div>
        </div>

        <div className="submissions-review__actions">
          <button
            type="button"
            className="btn subtle"
            onClick={this.dismissSubmission}
            disabled={decisionPending}
          >
            {decisionPending ? 'Dismissing…' : 'Mark as reviewed'}
          </button>
        </div>
      </div>
    );
  }

  render() {
    const { isOpen, onClose, chapter } = this.props;
    if (!isOpen) return null;

    return (
      <Modal isOpen={isOpen} onClose={onClose} fullscreen>
        <div className="submissions-modal">
          <header className="submissions-modal__header">
            <h2>Submissions for “{chapter?.title || 'chapter'}”</h2>
          </header>
          <div className="submissions-modal__body">
            <aside className="submissions-modal__aside">
              {this.renderList()}
            </aside>
            <main className="submissions-modal__main">
              {this.renderReview()}
            </main>
          </div>
        </div>
      </Modal>
    );
  }
}
